import { CreditCardStore } from "@infrastructure/stores/creditCard.store";
import { CreditCardClient } from "@infrastructure/clients/http/clients/creditCard.client";
import { CardInfo } from "@domain/dtos/models/card.models";
import { openLink } from "@telegram-apps/sdk";
import { closeMiniAppDelayed } from "@shared/integrations/telegram/init";
import { MachineStore } from "@infrastructure/stores/machine.store";

const CARD_BINDING_CONTEXT_KEY = "card_binding_context";

interface CardBindingContext {
  returnPath: string;
  backPath: string; // куда ведёт кнопка "назад"
  machineId: number | null;
  timestamp: number;
}

class CreditCardServiceClass {
  getStore() {
    return CreditCardStore;
  }

  /**
   * Получает список сохраненных карт
   */
  getCards(): CardInfo[] {
    return CreditCardStore.savedCards;
  }

  async fetchCards() {
    const creditCardClient = new CreditCardClient();
    const result = await creditCardClient.getCards();
    CreditCardStore.setCards(result.data.cards);
  }

  /**
   * Вычисляет куда должна вести кнопка "назад" с текущей страницы
   */
  private getBackPath(currentPath: string, machineId: number | null): string {
    const machineParam = machineId ? `?machineId=${machineId}` : "";
    
    // Логика навигации:
    // /payment -> /cart
    // /payment/settings -> /map (или /payment если есть machineId)
    // остальные -> /map
    if (currentPath.startsWith("/payment/settings")) {
      return machineId ? `/payment${machineParam}` : "/map";
    }
    if (currentPath.startsWith("/payment")) {
      return `/cart${machineParam}`;
    }
    return "/map";
  }

  /**
   * Сохраняет контекст перед переходом на привязку карты
   * Чтобы после возврата из Т-Банка знать куда вернуться
   */
  private saveBindingContext(): void {
    // Определяем текущий путь
    const hash = window.location.hash;
    const currentPath = hash.startsWith("#") ? hash.substring(1) : hash;
    
    // Получаем machineId из MachineStore
    const machineId = MachineStore.selectedId;
    
    // Вычисляем куда должна вести кнопка "назад"
    const backPath = this.getBackPath(currentPath, machineId);
    
    const context: CardBindingContext = {
      returnPath: currentPath || "/payment/settings",
      backPath,
      machineId,
      timestamp: Date.now(),
    };
    
    console.log("[CreditCardService] Saving binding context:", context);
    localStorage.setItem(CARD_BINDING_CONTEXT_KEY, JSON.stringify(context));
  }

  /**
   * Получает и очищает сохраненный контекст привязки карты
   */
  getAndClearBindingContext(): CardBindingContext | null {
    try {
      const raw = localStorage.getItem(CARD_BINDING_CONTEXT_KEY);
      if (!raw) return null;
      
      const context = JSON.parse(raw) as CardBindingContext;
      
      // Очищаем контекст после чтения
      localStorage.removeItem(CARD_BINDING_CONTEXT_KEY);
      
      // Проверяем что контекст не слишком старый (макс 30 минут)
      const MAX_AGE_MS = 30 * 60 * 1000;
      if (Date.now() - context.timestamp > MAX_AGE_MS) {
        console.log("[CreditCardService] Binding context expired, ignoring");
        return null;
      }
      
      console.log("[CreditCardService] Retrieved binding context:", context);
      return context;
    } catch (error) {
      console.error("[CreditCardService] Failed to get binding context:", error);
      return null;
    }
  }

  async addCard(): Promise<string> {
    const creditCardClient = new CreditCardClient();
    // Логируем токен для отладки проблемы с привязкой карты к неправильному пользователю
    const { authToken } = await import("@infrastructure/clients/http/authToken");
    const token = authToken.get();
    const tokenPreview = token ? (token.substring(0, 30) + "...") : "null";
    console.log("[CreditCardService.addCard] Adding card, token preview:", tokenPreview);
    
    // Сохраняем контекст ПЕРЕД переходом на банк
    // Чтобы после возврата знать куда вернуться и восстановить заказ
    this.saveBindingContext();
    
    const result = await creditCardClient.addCard();
    const bankUrl = result.data.payment_url;

    // Открываем ссылку через Telegram SDK
    // После редиректа от Т-Банка приложение откроется заново с startapp=cards
    console.log("[CreditCardService.addCard] Opening bank URL:", bankUrl);
    await openLink(bankUrl);
    
    // Закрываем Mini App с небольшой задержкой чтобы:
    // 1. Дать время браузеру открыться
    // 2. Избежать двух окон приложения
    // Т-Банк редиректит на t.me/phodo_ru_bot?startapp=cards
    console.log("[CreditCardService.addCard] Scheduling Mini App close");
    closeMiniAppDelayed(300);

    return bankUrl;
  }

  async setCardDefault(cardId: number) {
    const creditCardClient = new CreditCardClient();
    await creditCardClient.setCardDefault(cardId);
    CreditCardStore.setDefaultCard(cardId);
    await this.fetchCards();
  }

  async deleteCard(cardId: number) {
    const creditCardClient = new CreditCardClient();
    await creditCardClient.deleteCard(cardId);
    CreditCardStore.removeCard(cardId);
  }
}

export const CreditCardService = new CreditCardServiceClass();

